import { useState, useMemo, useEffect } from 'react';
import {
  Database,
  Plus,
  Pencil,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Share2,
  Lock,
  Users,
  Globe,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/utils/dateFormatter';
import { useViewportPageSize } from '@/hooks/use-viewport-page-size';
import { CollectionListSkeleton } from './KnowledgeBaseSkeletons';
import type { KbCollection } from '@/types/knowledge-base';

interface CollectionListViewProps {
  collections: KbCollection[];
  loading: boolean;
  searchQuery?: string;
  onCollectionClick: (collection: KbCollection) => void;
  onEditCollection?: (collection: KbCollection) => void;
  onDeleteCollection?: (collection: KbCollection) => void;
  onShareCollection?: (collection: KbCollection) => void;
  onCreateCollection: () => void;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
}

// Helper component for visibility badge
function VisibilityBadge({ collection }: { collection: KbCollection }) {
  const getVisibilityInfo = () => {
    switch (collection.visibility) {
      case 'public':
        return { icon: Globe, label: 'Public', variant: 'outline' as const };
      case 'team':
        const teamNames = collection.sharedTeams?.map((t) => t.teamName).join(', ') || 'Teams';
        return { icon: Users, label: teamNames, variant: 'secondary' as const };
      default:
        return { icon: Lock, label: 'Private', variant: 'secondary' as const };
    }
  };

  const { icon: Icon, label, variant } = getVisibilityInfo();
  const isTeam = collection.visibility === 'team';
  const displayLabel = isTeam && label.length > 20 ? label.substring(0, 17) + '...' : label;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant={variant} className="gap-1 cursor-default">
            <Icon className="h-3 w-3" />
            <span className="truncate max-w-[100px]">{displayLabel}</span>
          </Badge>
        </TooltipTrigger>
        {isTeam && collection.sharedTeams && collection.sharedTeams.length > 0 && (
          <TooltipContent>
            <p>Shared with: {collection.sharedTeams.map((t) => t.teamName).join(', ')}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

export function CollectionListView({
  collections,
  loading,
  searchQuery = '',
  onCollectionClick,
  onEditCollection,
  onDeleteCollection,
  onShareCollection,
  onCreateCollection,
  emptyStateTitle = 'No collections yet',
  emptyStateDescription = 'Create your first collection to get started',
}: CollectionListViewProps) {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = useViewportPageSize({
    rowHeight: 72,
    minPageSize: 5,
    maxPageSize: 50,
    bufferSpace: 5,
    loading,            // Recalculate when loading completes
  });

  // Sorting state
  const [sortColumn, setSortColumn] = useState<'name' | 'documents' | 'updated'>('updated');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, pageSize]);

  // Filter and sort collections
  const filteredCollections = useMemo(() => {
    const filtered = collections.filter((collection) => {
      if (!searchQuery) return true;
      const matchesSearch =
        collection.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        collection.description?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });

    // Sort the filtered results
    return filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'documents':
          comparison = a.documentCount - b.documentCount;
          break;
        case 'updated':
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [collections, searchQuery, sortColumn, sortDirection]);

  // Paginated collections
  const paginatedCollections = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredCollections.slice(startIndex, endIndex);
  }, [filteredCollections, currentPage, pageSize]);

  // Total pages
  const totalPages = Math.ceil(filteredCollections.length / pageSize);

  // Handle column sort
  const handleSort = (column: 'name' | 'documents' | 'updated') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-auto">
          <CollectionListSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Table or Empty State */}
      {filteredCollections.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center py-12 text-center">
          <div className="rounded-full bg-muted p-3 mb-4">
            <Database className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">
            {searchQuery ? 'No collections found' : emptyStateTitle}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery
              ? 'Try adjusting your search query'
              : emptyStateDescription}
          </p>
          {!searchQuery && (
            <Button onClick={onCreateCollection}>
              <Plus className="w-4 h-4 mr-2" />
              New Collection
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Table - Scrollable */}
          <div className="flex-1 overflow-auto">
            <div className="border rounded-lg bg-card shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[35%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('name')}
                      >
                        Name
                        {sortColumn === 'name' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </button>
                    </TableHead>
                    <TableHead className="w-[15%]">Visibility</TableHead>
                    <TableHead className="w-[10%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('documents')}
                      >
                        Documents
                        {sortColumn === 'documents' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </button>
                    </TableHead>
                    <TableHead className="w-[10%]">Chunks</TableHead>
                    <TableHead className="w-[15%]">
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort('updated')}
                      >
                        Updated
                        {sortColumn === 'updated' &&
                          (sortDirection === 'asc' ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </button>
                    </TableHead>
                    <TableHead className="w-[15%] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedCollections.map((collection) => (
                    <TableRow
                      key={collection.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => onCollectionClick(collection)}
                    >
                      <TableCell>
                        <div className={`flex flex-col gap-0.5 h-[40px] ${!collection.description ? 'justify-center' : ''}`}>
                          <div className="flex items-center gap-2">
                            <Database className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium">{collection.name}</span>
                          </div>
                          {collection.description && (
                            <span className="text-sm text-muted-foreground truncate max-w-md ml-6">
                              {collection.description}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <VisibilityBadge collection={collection} />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{collection.documentCount}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{collection.chunkCount}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(collection.updatedAt)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {collection.isOwner !== false && (
                          <div className="flex items-center justify-end gap-1">
                            {/* Share button */}
                            {onShareCollection && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onShareCollection(collection);
                                      }}
                                    >
                                      <Share2 className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Share settings</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {/* Edit button */}
                            {onEditCollection && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onEditCollection(collection);
                                      }}
                                    >
                                      <Pencil className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Edit collection</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {/* Delete button */}
                            {onDeleteCollection && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteCollection(collection);
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Delete collection</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination Footer - Fixed at bottom */}
          {filteredCollections.length > 0 && (
            <div className="flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-2 px-3 py-1.5 bg-card border rounded-lg mt-2">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                Showing {(currentPage - 1) * pageSize + 1} to{' '}
                {Math.min(currentPage * pageSize, filteredCollections.length)} of{' '}
                {filteredCollections.length} collections
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </Button>
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium whitespace-nowrap">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
